import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';
import Label from 'components/label/label';
import BilikZonesLayer, {
  BILIK_ZONE_CLASS_NAME,
} from 'components/map/bilik-zones-layer/bilik-zones-layer';
import Map from 'components/map/map';
import MapFullscreenControl from 'components/map/map-fullscreen-control/map-fullscreen-control';
import MapGeoJSON from 'components/map/map-geojson/map-geojson';
import MapLoading from 'components/map/map-loading/map-loading';
import MapModifierWheelZoom from 'components/map/map-modifier-wheel-zoom/map-modifier-wheel-zoom';
import { Feature, FeatureCollection, Geometry, Point } from 'geojson';
import { LatLng, Layer, LeafletMouseEvent } from 'leaflet';
import { ProPostalAddressField } from 'pages/pros/pro-form/pro-form-update-fields-type';
import React, { FunctionComponent } from 'react';
import { Tooltip } from 'react-leaflet';
import { Button, Icon } from 'semantic-ui-react';
import { excludeColor, includeColor } from 'utils/geometry';
import InterventionsFieldDepartmentsLayer from './interventions-field-departments-layer';
import {
  DepartmentProperties,
  FeatureDepartment,
  FeatureIntervention,
  OtherProperties,
} from './interventions-field-repository';
import InterventionsFieldToolbar, {
  SelectionMode,
} from './interventions-field-toolbar';
import './interventions-field.css';

const getGeometryStyle = (feature?: Feature<Geometry>) => {
  switch (feature?.properties?.type) {
    case 'include':
      return {
        color: includeColor,
        fillColor: includeColor,
        fillOpacity: 0.3,
        stroke: true,
      };
    case 'exclude':
      return {
        color: excludeColor,
        fillColor: excludeColor,
        fillOpacity: 0.3,
        stroke: true,
      };
    case 'touches':
      return {
        color: '#000000',
        fillColor: '#000000',
        fillOpacity: 0,
        stroke: true,
      };
    default:
      return {
        color: '#ffffff',
        fillColor: '#ffffff',
        fillOpacity: 0.1,
        stroke: true,
      };
  }
};

type InterventionsFieldViewProps = {
  label?: string;
  proPostalAddress?: ProPostalAddressField;
  loadingInterventions?: boolean;
  selectionMode: SelectionMode;
  canDraw: boolean;
  onChangeSelectionMode: (selectionMode: SelectionMode) => void;
  departments: FeatureCollection<Geometry, DepartmentProperties>;
  onEachDepartmentFeature: (feature: FeatureDepartment, layer: Layer) => void;
  onDeleteFeatures: () => void;
  otherFeatures?: FeatureCollection<Geometry, OtherProperties>;
  displayBilikZones?: boolean;
  defaultMapCenter?: LatLng | null;
  onPolygonDrawed: (e: { shape: string; layer: Layer }) => void;
  interventions: FeatureCollection<Geometry>;
  onClickMap: (e: LeafletMouseEvent) => void;
  onEachCityInterventionFeature: (
    feature: FeatureIntervention,
    layer: Layer,
  ) => void;
};

const InterventionsFieldView: FunctionComponent<
  InterventionsFieldViewProps
> = ({
  proPostalAddress,
  selectionMode,
  canDraw,
  onChangeSelectionMode,
  departments,
  onEachDepartmentFeature,
  otherFeatures,
  displayBilikZones,
  defaultMapCenter,
  interventions,
  loadingInterventions,
  label,
  onDeleteFeatures,
  onClickMap,
  onPolygonDrawed,
  onEachCityInterventionFeature,
}) => {
  const hasNamedOtherFeatures = !!otherFeatures?.features.some(
    (feature) => feature.properties.name,
  );

  return (
    <>
      {label && <Label>{label}</Label>}
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
        }}
      >
        <div>
          {selectionMode === 'lasso' && (
            <p style={{ color: 'green', padding: 0, margin: 0 }}>
              Dessinez une zone ou sélectionnez des départements à l&apos;aide
              des boutons situés en bas à droite
            </p>
          )}
          {selectionMode === 'department' && (
            <p style={{ color: 'green', padding: 0, margin: 0 }}>
              Cliquez sur un département pour sélectionner toutes ses villes,
              cliquez à nouveau pour les retirer
            </p>
          )}
          {selectionMode === 'click' && (
            <p style={{ color: 'green', padding: 0, margin: 0 }}>
              Cliquez sur une ville pour la sélectionner, vous pouvez cliquer
              sur une zone vide pour ajouter une ville
            </p>
          )}
        </div>
        {interventions.features.some((intervention) =>
          ['include', 'exclude'].includes(intervention.properties?.type),
        ) && (
          <Button negative basic type="button" onClick={onDeleteFeatures}>
            <Icon name="trash alternate outline" />
            Supprimer toutes les villes
          </Button>
        )}
      </div>

      <Map
        style={{ height: `700px` }}
        doubleClickZoom={false}
        zoom={10}
        // Fitted on the cities of the zone once they are loaded
        fitReady={!loadingInterventions}
        eventHandlers={{
          click: (e) => onClickMap(e),
        }}
        center={defaultMapCenter ?? [50, 3]}
      >
        <MapFullscreenControl />
        <MapModifierWheelZoom />
        {displayBilikZones && <BilikZonesLayer />}
        {loadingInterventions && <MapLoading />}
        <InterventionsFieldToolbar
          selectionMode={selectionMode}
          canDraw={canDraw}
          onChangeSelectionMode={onChangeSelectionMode}
          onPolygonDrawed={onPolygonDrawed}
        />
        {interventions.features.map((intervention) => {
          return (
            <MapGeoJSON
              // The selection mode is in the key to bind the click handler
              // only in the cities selection mode
              key={`city-${intervention.properties?.id}-${intervention.properties?.type}-${selectionMode}`}
              data={intervention}
              onEachFeature={
                selectionMode === 'click'
                  ? onEachCityInterventionFeature
                  : undefined
              }
              pmIgnore={true}
              // Let the click go through to the department below
              interactive={selectionMode !== 'department'}
              style={getGeometryStyle(intervention)}
            >
              <Tooltip direction="top" offset={[0, -10]} opacity={1}>
                {intervention.properties?.name}
              </Tooltip>
            </MapGeoJSON>
          );
        })}
        {selectionMode === 'department' && (
          <InterventionsFieldDepartmentsLayer
            departments={departments}
            onEachDepartmentFeature={onEachDepartmentFeature}
          />
        )}
        {proPostalAddress?.geoCoordinates &&
        proPostalAddress?.displayOption === 'complete' ? (
          <MapGeoJSON
            data={proPostalAddress.geoCoordinates as Point}
            interactive={false}
            pmIgnore={true}
          />
        ) : null}
        {proPostalAddress?.cityGeoCoordinates &&
        proPostalAddress?.displayOption === 'city' ? (
          <MapGeoJSON
            data={proPostalAddress.cityGeoCoordinates as Point}
            interactive={false}
            pmIgnore={true}
          />
        ) : null}
        {otherFeatures && (
          <MapGeoJSON
            key={`other-features`}
            data={otherFeatures}
            style={(feature) => ({
              color: feature?.properties?.color,
              fillColor: feature?.properties?.fillColor,
              fillOpacity: feature?.properties?.fillOpacity,
              stroke: feature?.properties?.stroke,
              ...(feature?.properties?.weight !== undefined && {
                weight: feature.properties.weight,
              }),
              className: feature?.properties?.name
                ? BILIK_ZONE_CLASS_NAME
                : undefined,
            })}
            pmIgnore={true}
            // Only named features (bilik zones) are hoverable, the others must
            // not catch the clicks and hovers of the cities below
            interactive={hasNamedOtherFeatures}
            onEachFeature={(feature, layer) => {
              if (feature.properties.name) {
                layer.bindTooltip(feature.properties.name, { sticky: true });
              }
            }}
          />
        )}
      </Map>
    </>
  );
};

export default InterventionsFieldView;
